import type { StatusDefinitionRow } from '../statusDefinitions';
import { isoDay } from '../zonedTime';

/**
 * Accounting periods (piece 23; decision 0196): one row per month of the
 * fiscal year, cut from Organization.fiscalYearStart and created on demand.
 * A Closed row is the lock; the lock reads JournalEntry.accrualDate; a period
 * closes only when every earlier period is Closed; there is no override that
 * posts past it — an Admin reopens with a reason, posts, and closes again.
 */
export const ACCOUNTING_PERIOD_STATUSES = ['Open', 'Closed'] as const;
export type AccountingPeriodStatusValue = (typeof ACCOUNTING_PERIOD_STATUSES)[number];

export const ACCOUNTING_PERIOD_STATUS_DEFINITIONS: readonly StatusDefinitionRow[] = [
  { entityType: 'AccountingPeriodStatus', statusValue: 'Open', label: 'Open', colorGroup: 'good', sortOrder: 1 },
  { entityType: 'AccountingPeriodStatus', statusValue: 'Closed', label: 'Closed', colorGroup: 'inactive', sortOrder: 2 },
];

/** The two StatusTransition rows: Open to Closed by the Close action, Closed to Open by an Admin reopen, and no other move. */
export const ACCOUNTING_PERIOD_TRANSITIONS: readonly { entityType: string; from: string; to: string }[] = [
  { entityType: 'AccountingPeriodStatus', from: 'Open', to: 'Closed' },
  { entityType: 'AccountingPeriodStatus', from: 'Closed', to: 'Open' },
];

/**
 * 🔒 How long an accounting period runs (decision 0456). A month is the
 * default and was the only length until 2026-09-16; a quarter and a year are
 * what a business closing less often actually keeps its books on.
 */
export const ACCOUNTING_PERIOD_CADENCES = ['Monthly', 'Quarterly', 'Annual'] as const;
export type AccountingPeriodCadenceValue = (typeof ACCOUNTING_PERIOD_CADENCES)[number];

/** The months one period of each cadence covers; a fiscal year is a whole number of every one of them. */
export const CADENCE_MONTHS: Readonly<Record<AccountingPeriodCadenceValue, number>> = { Monthly: 1, Quarterly: 3, Annual: 12 };

export const CADENCE_LABELS: Readonly<Record<AccountingPeriodCadenceValue, string>> = {
  Monthly: 'Monthly — twelve periods a year',
  Quarterly: 'Quarterly — four periods a year',
  Annual: 'Annually — one period a year',
};

export function isAccountingPeriodCadence(value: unknown): value is AccountingPeriodCadenceValue {
  return typeof value === 'string' && (ACCOUNTING_PERIOD_CADENCES as readonly string[]).includes(value);
}

export interface PeriodBounds {
  fiscalYear: number;
  /** The first day, as an ISO date. */
  periodStart: string;
  /** The last day, as an ISO date. */
  periodEnd: string;
}

const iso = (y: number, m: number, d: number): string => new Date(Date.UTC(y, m, d)).toISOString().slice(0, 10);

/**
 * The month a date falls in, cut from the fiscal year's start day rather than
 * the calendar's first. A fiscal year starting 1 July names its periods by the
 * calendar year the fiscal year ends in, the convention every year-to-date
 * figure follows; a January start names them by the calendar year itself.
 */
export function periodFor(date: Date, fiscalYearStart: Date, cadence: AccountingPeriodCadenceValue = 'Monthly'): PeriodBounds {
  const startMonth = fiscalYearStart.getUTCMonth();
  const startDay = fiscalYearStart.getUTCDate();
  let y = date.getUTCFullYear();
  let m = date.getUTCMonth();
  if (date.getUTCDate() < startDay) {
    m -= 1;
    if (m < 0) {
      m = 11;
      y -= 1;
    }
  }
  /**
   * 🔨 The period is counted from the fiscal year's own first month, never
   * from January (decision 0456). A quarter of a fiscal year starting in July
   * runs July to September, and a calendar quarter would cut it across the
   * year-end figure every report reads.
   */
  const months = CADENCE_MONTHS[cadence];
  const anchor = new Date(`${fiscalYearStartFor(date, fiscalYearStart)}T00:00:00Z`);
  const sinceAnchor = (y - anchor.getUTCFullYear()) * 12 + (m - anchor.getUTCMonth());
  const firstMonth = anchor.getUTCMonth() + Math.floor(sinceAnchor / months) * months;
  const periodStart = iso(anchor.getUTCFullYear(), firstMonth, startDay);
  const next = new Date(Date.UTC(anchor.getUTCFullYear(), firstMonth + months, startDay));
  const periodEnd = new Date(next.getTime() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const fiscalYearStartsThisYear = new Date(Date.UTC(y, startMonth, startDay));
  const fiscalYear = startMonth === 0 && startDay === 1 ? y : new Date(periodStart).getTime() >= fiscalYearStartsThisYear.getTime() ? y + 1 : y;
  return { fiscalYear, periodStart, periodEnd };
}

/** The first day of the fiscal year a date falls in, the boundary between retained earnings' prior periods and the current range. */
export function fiscalYearStartFor(date: Date, fiscalYearStart: Date): string {
  const startMonth = fiscalYearStart.getUTCMonth();
  const startDay = fiscalYearStart.getUTCDate();
  const thisYear = new Date(Date.UTC(date.getUTCFullYear(), startMonth, startDay));
  return (date.getTime() >= thisYear.getTime() ? thisYear : new Date(Date.UTC(date.getUTCFullYear() - 1, startMonth, startDay))).toISOString().slice(0, 10);
}

/**
 * Every period of one fiscal year up to and including the one a date falls in,
 * as first days in order. 🔨 A close materialises these before it reads the
 * run (found in review, 2026-09-15): a period row is created on demand, so a
 * February nothing posted into does not exist, and a March close saw an
 * unbroken run of Closed months that had a hole in it.
 */
export function periodStartsThroughFiscalYear(date: Date, fiscalYearStart: Date, cadence: AccountingPeriodCadenceValue = 'Monthly'): string[] {
  const target = periodFor(date, fiscalYearStart, cadence).periodStart;
  const months = CADENCE_MONTHS[cadence];
  const out: string[] = [];
  let cursor = fiscalYearStartFor(date, fiscalYearStart);
  // A fiscal year holds twelve months, so it holds 12/months periods; the extra step is the target's own and the bound is a guard.
  for (let step = 0; step <= 12 / months && cursor <= target; step += 1) {
    out.push(cursor);
    const at = new Date(`${cursor}T00:00:00Z`);
    cursor = iso(at.getUTCFullYear(), at.getUTCMonth() + months, at.getUTCDate());
  }
  return out;
}

export interface PeriodLike {
  periodStart: string | Date;
  status: AccountingPeriodStatusValue | string;
}

/** 🔨 A period closes only when every earlier period is Closed; the reason, when it cannot, names the first open month before it. */
export function closeProblem(target: PeriodLike, all: readonly PeriodLike[]): string | null {
  if (target.status === 'Closed') return 'the period is already Closed';
  const earlierOpen = all.filter((p) => isoDay(p.periodStart) < isoDay(target.periodStart) && p.status !== 'Closed').sort((a, b) => isoDay(a.periodStart).localeCompare(isoDay(b.periodStart)));
  const first = earlierOpen[0];
  if (first) return `the period beginning ${isoDay(first.periodStart)} is still Open; periods close in order`;
  return null;
}

/**
 * 🔒 The cadence changes only while nothing has been closed (decision 0456).
 * A closed month inside a new quarter is a lock nothing can read: the lock is
 * a row keyed on its own first day, and re-cutting the year moves every first
 * day. The Open rows are re-cut on the change and carry nothing; a Closed one
 * is a decision somebody made and is never re-cut.
 */
export function cadenceChangeProblem(
  next: unknown,
  current: AccountingPeriodCadenceValue,
  periods: readonly PeriodLike[],
): string | null {
  if (!isAccountingPeriodCadence(next)) return `a period runs one of ${ACCOUNTING_PERIOD_CADENCES.join(', ')}`;
  if (next === current) return null;
  const closed = periods.filter((p) => p.status === 'Closed').sort((a, b) => isoDay(a.periodStart).localeCompare(isoDay(b.periodStart)));
  const first = closed[0];
  if (first) return `the period beginning ${isoDay(first.periodStart)} is Closed, so the periods cannot be re-cut; reopen every closed period first`;
  return null;
}

/** A reopen carries a reason, and only a Closed period reopens. */
export function reopenProblem(target: PeriodLike, reason: string | null | undefined): string | null {
  if (target.status !== 'Closed') return 'only a Closed period reopens';
  if (!reason || !reason.trim()) return 'a reopen records why';
  return null;
}

/** The sentence a document save is refused with when its date lands in a Closed period: the period is named, not a code. */
export function periodClosedMessage(bounds: { periodStart: string | Date; periodEnd: string | Date }): string {
  return `the accounting period ${isoDay(bounds.periodStart)} to ${isoDay(bounds.periodEnd)} is Closed; an Admin reopens it with a reason before anything posts into it`;
}
